/**
 * WebSourcesBlock.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-250 · Phase 5E-3b · Query Thread Render Extraction · WEB RESULTS sub-region
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Purpose:
 *   Extract the WEB RESULTS conditional JSX block from the KnowledgeGraphPage.jsx
 *   queryThread render tree. Second slice of the five-sub-slice Phase 5E-3
 *   programme. The block is guarded by
 *     item.result.web_results && item.result.web_results.length > 0
 *       && item.result.type !== 'product_search'
 *   and covers the header row (title · Safety-View toggle · List-vs-Circles
 *   toggle), the Web/Images/Videos three-button tab strip, the Web-tab body
 *   (internal-docs prompt · list view · circles view · show-more toggle), and
 *   the Images and Videos tab bodies.
 *
 * Props (17):
 *   item                    · one queryThread entry · owns item.result.web_results,
 *                              item.result.has_kg_content, item.result.type, item.query
 *   idx                     · thread-item index (keys every data-testid and every
 *                              media-tab state read)
 *   isMobile                · mobile-vs-desktop layout branch
 *   browseMode              · 'safety' | 'direct'
 *   setBrowseMode           · setter
 *   webViewMode             · 'list' | 'circles'
 *   setWebViewMode          · setter
 *   webResultsExpanded      · Set<idx> · which thread items are expanded past first 5
 *   setWebResultsExpanded   · setter
 *   mediaTab                · from useMediaTabs hook · dictionary keyed by idx
 *   mediaCache              · from useMediaTabs hook
 *   mediaLoading            · from useMediaTabs hook
 *   mediaError              · from useMediaTabs hook
 *   switchMediaTab          · tab-button click handler owned by parent
 *   setSafeBrowserState     · opens Safe Browser overlay
 *   setLightbox             · opens image lightbox
 *   handleQuery             · used by the 'View internal' button
 *
 * Byte-verbatim on every className · style · data-testid · axios URL ·
 * callback body. Zero behaviour change.
 */
import React from 'react';
import { Globe, Image as ImageIcon, Video, Shield, List, LayoutGrid, FileText, ExternalLink, Play } from 'lucide-react';
import { decodeExcerpt } from '../../lib/htmlDecode';

export function WebSourcesBlock({
  item,
  idx,
  isMobile,
  browseMode,
  setBrowseMode,
  webViewMode,
  setWebViewMode,
  webResultsExpanded,
  setWebResultsExpanded,
  mediaTab,
  mediaCache,
  mediaLoading,
  mediaError,
  switchMediaTab,
  setSafeBrowserState,
  setLightbox,
  handleQuery,
}) {
  if (!item.result.web_results || item.result.web_results.length === 0 || item.result.type === 'product_search') {
    return null;
  }
  return (
    <div className="mb-6 pb-5 border-b border-white/10" data-testid={`web-results-${idx}`}>
      {/* Header with browse mode and view toggle */}
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'} mb-3`} style={{ overflow: 'visible' }}>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-cyan-600" />
            <h4 className={`font-bold text-white ${isMobile ? 'text-sm' : 'text-base'} tracking-wide whitespace-nowrap`}>From the Web</h4>
            <span className="text-xs text-gray-300 bg-white/10 px-2 py-0.5 rounded-full">{item.result.web_results.length} results</span>
          </div>
          {/* Safety View toggle — left-aligned with "From the Web" header and the Web|Images|Videos tab row below */}
          <div className="relative group" data-testid="browse-mode-toggle">
            <button
              onClick={() => setBrowseMode(browseMode === 'safety' ? 'direct' : 'safety')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                browseMode === 'safety'
                  ? 'bg-emerald-600/90 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid="browse-mode-safety"
            >
              <Shield className="h-3.5 w-3.5" />
              Safety View {browseMode === 'safety' ? 'ON' : 'OFF'}
            </button>
            {!isMobile && (
              <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '380px' }}>
                <p className="text-lg text-gray-200 leading-relaxed">Safety View allows you to view a website home page without actually visiting it.</p>
              </div>
            )}
          </div>
        </div>

        {/* View toggle — list vs circles */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setWebViewMode('list')}
              className={`p-1.5 rounded transition-colors ${webViewMode === 'list' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
              title="List view"
              data-testid={`web-list-view-btn-${idx}`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setWebViewMode('circles')}
              className={`p-1.5 rounded transition-colors ${webViewMode === 'circles' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
              title="Circle view"
              data-testid={`web-circle-view-btn-${idx}`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Multi-modal tabs: Web / Images / Videos */}
      {(() => {
        const activeTab = mediaTab[idx] || 'web';
        return (
          <div className="flex items-center gap-1 mb-4 border-b border-white/10" data-testid={`media-tabs-${idx}`}>
            {[
              { key: 'web', label: 'Web', icon: Globe },
              { key: 'images', label: 'Images', icon: ImageIcon },
              { key: 'videos', label: 'Videos', icon: Video },
            ].map(t => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => switchMediaTab(idx, t.key, item.query)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-all border-b-2 -mb-px ${
                    isActive
                      ? 'text-cyan-300 border-cyan-400'
                      : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                  }`}
                  data-testid={`media-tab-${t.key}-${idx}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        );
      })()}

      {(mediaTab[idx] || 'web') === 'web' && (<>
      {/* Internal docs prompt */}
      {item.result.has_kg_content && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border text-sm" style={{ backgroundColor: 'rgba(6,182,212,0.07)', borderColor: 'rgba(6,182,212,0.25)' }} data-testid={`kg-prompt-${idx}`}>
          <FileText className="h-4 w-4 text-cyan-600 flex-shrink-0" />
          <span className="text-gray-200">Your knowledge graph has related documents.</span>
          <button
            className="ml-auto text-cyan-600 hover:text-cyan-800 font-semibold whitespace-nowrap text-xs transition-colors"
            onClick={() => handleQuery(`Show me internal documents only about: ${item.query}`, true)}
            data-testid={`kg-internal-btn-${idx}`}
          >
            View internal &rarr;
          </button>
        </div>
      )}

      {/* List View for Web Results */}
      {webViewMode === 'list' && (
        <div className="space-y-2" data-testid={`web-list-view-${idx}`}>
          {(webResultsExpanded.has(idx) ? item.result.web_results : item.result.web_results.slice(0, 5)).map((result, wIdx) => {
            const clearbitUrl = result.domain ? `https://logo.clearbit.com/${result.domain}` : null;
            return (
              <div
                key={wIdx}
                onClick={(e) => {
                  e.preventDefault();
                  if (browseMode === 'safety') {
                    setSafeBrowserState({ url: result.url, title: result.title });
                  } else {
                    window.open(result.url, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all hover:shadow-md cursor-pointer border border-transparent hover:border-cyan-300"
                data-testid={`web-list-item-${idx}-${wIdx}`}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: '#00d4ff' }}
                >
                  {clearbitUrl ? (
                    <img
                      src={clearbitUrl}
                      alt={result.source_name}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Globe className="h-6 w-6 text-white" style={{ display: clearbitUrl ? 'none' : 'block' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-100 line-clamp-1">{result.title}</p>
                  <p className="text-xs text-cyan-300 mb-1">{result.source_name}</p>
                  <p className="text-xs text-gray-400 line-clamp-2">{decodeExcerpt(result.snippet)}</p>
                </div>
                <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}

      {/* Circle View for Web Results */}
      {webViewMode === 'circles' && (
        <div className={`web-circle-row ${isMobile ? 'flex flex-nowrap overflow-x-auto gap-3 pb-2' : ''}`} style={isMobile ? { WebkitOverflowScrolling: 'touch' } : {}} data-testid={`web-circle-view-${idx}`}>
          {(webResultsExpanded.has(idx) ? item.result.web_results : item.result.web_results.slice(0, 5)).map((result, wIdx) => {
            const BRAND_BLUE = '#00d4ff';
            const clearbitUrl = result.domain ? `https://logo.clearbit.com/${result.domain}` : null;
            const snippet = decodeExcerpt(result.snippet || result.title || '').substring(0, 130);
            const srcName = result.source_name || '';
            const nameFontSize = srcName.length > 13 ? '0.7rem' : srcName.length > 9 ? '0.9rem' : '1.15rem';
            const mobileNameSize = srcName.length > 13 ? '0.55rem' : srcName.length > 9 ? '0.65rem' : '0.8rem';
            return (
              <div
                key={wIdx}
                className={`web-circle-item ${isMobile ? 'flex-shrink-0' : ''}`}
                data-testid={`web-circle-${idx}-${wIdx}`}
                title={result.title}
                style={{ cursor: 'pointer', width: isMobile ? '100px' : undefined, minWidth: isMobile ? '100px' : undefined }}
                onClick={() => {
                  if (browseMode === 'safety') {
                    setSafeBrowserState({ url: result.url, title: result.title });
                  } else {
                    window.open(result.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <div className="web-circle-disk-wrapper" style={{ width: isMobile ? '72px' : undefined, height: isMobile ? '72px' : undefined }}>
                <div
                  className="web-circle-disk shadow-lg"
                  style={{ backgroundColor: BRAND_BLUE, width: isMobile ? '72px' : undefined, height: isMobile ? '72px' : undefined }}
                >
                  {/* Cloud wisp shimmer — each circle staggered */}
                  <div
                    className="shimmer-glass-subtle"
                    style={{ '--wisp-delay': `${wIdx * 1.8}s` }}
                  ></div>

                  {/* Image: Clearbit → apple-touch-icon → Google favicon → source text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                    {clearbitUrl ? (
                      <img
                        src={clearbitUrl}
                        alt={srcName}
                        style={{ width: '55%', height: '55%', objectFit: 'contain', borderRadius: '18%' }}
                        onError={(e) => {
                          const domain = result.domain;
                          if (e.target.src.includes('logo.clearbit.com')) {
                            e.target.src = `https://${domain}/apple-touch-icon.png`;
                          } else if (e.target.src.includes('apple-touch-icon')) {
                            e.target.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
                            e.target.style.borderRadius = '0';
                            e.target.style.width = '38%';
                            e.target.style.height = '38%';
                          } else {
                            e.target.style.display = 'none';
                            const fb = document.getElementById(`wcf-${idx}-${wIdx}`);
                            if (fb) fb.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    {/* Text fallback — shown when all images fail */}
                    <div
                      id={`wcf-${idx}-${wIdx}`}
                      style={{ display: clearbitUrl ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 14px', textAlign: 'center' }}
                    >
                      <span className="text-white font-bold leading-tight" style={{ fontSize: isMobile ? mobileNameSize : nameFontSize }}>{srcName}</span>
                    </div>
                  </div>

                  {/* Snippet — visible at 2× hover scale */}
                  <div className="web-circle-snippet">
                    <p className="text-white text-center leading-snug" style={{ fontSize: '0.44rem', lineHeight: 1.5 }}>
                      {snippet}
                    </p>
                  </div>
                </div>
                </div>

                {/* Label below — black numbered badge replaces the bracketed [N] */}
                <p className="font-bold text-center flex items-center justify-center gap-1.5" style={{ color: '#ffffff', maxWidth: isMobile ? '100px' : '160px', fontSize: isMobile ? '0.75rem' : '1rem' }}>
                  <span
                    className="inline-flex items-center justify-center rounded-full flex-shrink-0"
                    style={{
                      width: isMobile ? '18px' : '22px',
                      height: isMobile ? '18px' : '22px',
                      backgroundColor: '#67e8f9',
                      color: '#0b1220',
                      fontSize: isMobile ? '10px' : '12px',
                      fontWeight: 800,
                      border: '2px solid rgba(8,18,32,0.35)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                    }}
                    data-testid={`web-citation-badge-${idx}-${wIdx}`}
                  >
                    {wIdx + 1}
                  </span>
                  <span className="truncate">{srcName}</span>
                </p>
                <p className="text-center leading-tight line-clamp-2 px-1" style={{ fontSize: isMobile ? '0.7rem' : '0.875rem', color: '#e5e7eb', maxWidth: isMobile ? '100px' : '160px' }}>
                  {result.title.length > (isMobile ? 30 : 55) ? result.title.substring(0, isMobile ? 28 : 55) + '...' : result.title}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Show more / less */}
      {item.result.web_results.length > 5 && (
        <button
          className="mt-3 text-xs text-gray-200 hover:text-white font-medium transition-colors"
          onClick={() => setWebResultsExpanded(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
          })}
          data-testid={`web-expand-btn-${idx}`}
        >
          {webResultsExpanded.has(idx) ? 'Show fewer results' : `+ Show ${item.result.web_results.length - 5} more results`}
        </button>
      )}
      </>)}

      {/* IMAGES TAB */}
      {(mediaTab[idx] || 'web') === 'images' && (
        <div data-testid={`media-images-${idx}`}>
          {mediaLoading[idx] === 'images' && (
            <div className="flex items-center justify-center py-10 text-gray-500 text-sm gap-2">
              <div className="h-4 w-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              Loading images...
            </div>
          )}
          {mediaError[idx]?.images && (
            <div className="text-red-500 text-sm py-4">{mediaError[idx].images}</div>
          )}
          {!mediaLoading[idx] && !mediaError[idx]?.images && (mediaCache[idx]?.images?.length || 0) === 0 && (
            <div className="text-gray-500 text-sm py-4">No images found.</div>
          )}
          {(mediaCache[idx]?.images || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {mediaCache[idx].images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox({ src: img.image, thumb: img.thumbnail, title: img.title, sourceUrl: img.url, source: img.source })}
                  className="group relative block aspect-square overflow-hidden rounded-lg bg-gray-200/40 border border-gray-300/30 hover:border-cyan-500 transition-all"
                  data-testid={`media-image-${idx}-${i}`}
                  title={img.title}
                >
                  <img
                    src={img.thumbnail || img.image}
                    alt={img.title}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-[10px] truncate">{img.source || 'web'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VIDEOS TAB */}
      {(mediaTab[idx] || 'web') === 'videos' && (
        <div data-testid={`media-videos-${idx}`}>
          {mediaLoading[idx] === 'videos' && (
            <div className="flex items-center justify-center py-10 text-gray-500 text-sm gap-2">
              <div className="h-4 w-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              Loading videos...
            </div>
          )}
          {mediaError[idx]?.videos && (
            <div className="text-red-500 text-sm py-4">{mediaError[idx].videos}</div>
          )}
          {!mediaLoading[idx] && !mediaError[idx]?.videos && (mediaCache[idx]?.videos?.length || 0) === 0 && (
            <div className="text-gray-500 text-sm py-4">No videos found.</div>
          )}
          {(mediaCache[idx]?.videos || []).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {mediaCache[idx].videos.map((vid, i) => (
                <a
                  key={i}
                  href={vid.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-lg overflow-hidden bg-gray-100/30 border border-gray-300/30 hover:border-cyan-500 transition-all"
                  data-testid={`media-video-${idx}-${i}`}
                >
                  <div className="relative aspect-video bg-black overflow-hidden">
                    {vid.thumbnail ? (
                      <img src={vid.thumbnail} alt={vid.title} loading="lazy" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-800" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                      <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="h-5 w-5 text-cyan-700 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                    {vid.duration && (
                      <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/80 text-white text-[10px] font-semibold">{vid.duration}</span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-semibold text-gray-100 line-clamp-2 group-hover:text-cyan-300 transition-colors">{vid.title}</p>
                    <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-500">
                      <span className="truncate">{vid.uploader || vid.publisher || 'web'}</span>
                      {vid.view_count != null && (
                        <span className="ml-2 whitespace-nowrap">{Number(vid.view_count).toLocaleString()} views</span>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
